import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  LineChart,
  Handshake,
  Leaf,
  Fuel,
  Sun,
  Building2,
  FileText,
  FlaskConical,
  Network,
  ShieldCheck,
  Droplets,
  Layers,
} from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import Nav from "@/components/site/Nav";
import { Reveal } from "@/components/site/Reveal";
import SectionTabs from "@/components/site/SectionTabs";

// Mappa icona per ogni area (chart.id). Solo presentazione: i contenuti restano dal CMS.
const AREA_ICONS = {
  // energy-management
  "analisi-controllo": LineChart,
  "procurement-mercato": Handshake,
  "green-traceability": Leaf,
  "combustibili-lubrificanti-fluidi": Fuel,
  // efficiency-transition
  "analisi-progettazione": LineChart,
  "produzione-autoconsumo-mobilita": Sun,
  "impianti-involucro": Building2,
  "incentivi-pratiche": FileText,
  "efficienza-combustibili-chimica": FlaskConical,
  // technical-risk
  "coordinamento-consulenza": Network,
  "assicurazioni-rischio": ShieldCheck,
  "servizi-ambientali-siti": Droplets,
  "pratiche-agevolazioni-fiscalita": FileText,
};

function ChartContent({ chart, lang }) {
  const services = (chart.services || []).filter((s) => s.stato !== "bozza");
  return (
    <div data-testid={`chart-${chart.id}`}>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice">{tx(chart.title, lang)}</h2>
      {tx(chart.note, lang) && (
        <p className="mt-5 border-l-2 border-royal pl-4 text-sm text-muted-foreground max-w-3xl leading-relaxed bg-onyx/40 py-3 pr-4 rounded-r-lg">
          {tx(chart.note, lang)}
        </p>
      )}
      <div className="mt-7 grid md:grid-cols-2 gap-4">
        {services.map((s) => (
          <div
            key={s.slug || tx(s.title, lang)}
            data-testid={`chart-service-${s.slug}`}
            className="h-full bg-onyx/40 border border-ice/8 rounded-xl p-5 hover:border-royal/50 transition-colors"
          >
            <div className="flex items-start gap-3.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-royal/15 text-azure">
                <Check size={13} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ice leading-snug">{tx(s.title, lang)}</p>
                {tx(s.desc, lang) && <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{tx(s.desc, lang)}</p>}
                {s.partner && (
                  <p className="mt-2.5 font-mono text-[11px] uppercase tracking-[0.12em] text-royal">
                    {lang === "it" ? "Con" : "With"} {s.partner}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {tx(chart.label, lang) && (
        <p
          data-testid={`chart-${chart.id}-label`}
          className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.15em] text-royal border border-royal/25 rounded-full px-3.5 py-1.5 bg-royal/5"
        >
          {tx(chart.label, lang)}
        </p>
      )}
      {tx(chart.note_footer, lang) && (
        <p className="mt-6 font-mono text-xs text-muted-foreground/80 max-w-3xl leading-relaxed">{tx(chart.note_footer, lang)}</p>
      )}
    </div>
  );
}

export default function MacroareaPage() {
  const { id } = useParams();
  const { content, lang } = useSite();
  const area = content.macroaree?.[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!area) {
    return (
      <div className="min-h-screen bg-onyx text-ice flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">{lang === "it" ? "Pagina non trovata." : "Page not found."}</p>
          <Link to="/" className="mt-4 inline-block text-azure hover:underline">
            ← {lang === "it" ? "Torna alla home" : "Back to home"}
          </Link>
        </div>
      </div>
    );
  }

  const charts = (area.charts || []).filter((c) => c.stato !== "bozza");
  const cta = area.cta_finale || {};
  const trasversale = area.trasversale || null;

  const chartItems = charts.map((c) => {
    const Icon = AREA_ICONS[c.id] || Layers;
    return {
      id: c.id || c.num,
      badge: <Icon size={18} />,
      title: tx(c.title, lang),
      subtitle: tx(c.sub, lang),
      render: () => <ChartContent chart={c} lang={lang} />,
    };
  });

  return (
    <div className="min-h-screen bg-onyx text-ice">
      <Nav />

      <div className="relative h-[420px] lg:h-[520px] overflow-hidden" data-testid={`macroarea-page-${id}`}>
        {area.image && (
          <img src={area.image} alt={tx(area.title, lang)} className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/60 to-onyx/15" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 h-full flex flex-col justify-end pb-12">
          <Reveal>
            <span className="font-mono text-xs font-semibold tracking-[0.2em] text-royal uppercase">
              {lang === "it" ? "Soluzioni" : "Solutions"}
            </span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-ice max-w-4xl">
              {tx(area.title, lang)}
            </h1>
            {tx(area.payoff, lang) && (
              <p className="mt-4 font-serif italic text-lg sm:text-xl text-azure max-w-2xl">{tx(area.payoff, lang)}</p>
            )}
          </Reveal>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-6 pb-10">
        <Reveal className="max-w-3xl pt-14 lg:pt-16">
          <p className="text-base text-muted-foreground leading-relaxed">{tx(area.intro, lang)}</p>
        </Reveal>

        {chartItems.length > 0 && (
          <div className="pt-12 lg:pt-14" data-testid="macroarea-aree">
            <SectionTabs items={chartItems} ariaLabel={lang === "it" ? "Aree della soluzione" : "Solution areas"} testIdPrefix="area" />
          </div>
        )}

        {trasversale && tx(trasversale.title, lang) && (
          <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId="macroarea-trasversale">
            <div className="bg-ice text-white rounded-2xl p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-royal/30 blur-3xl" />
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight max-w-3xl">{tx(trasversale.title, lang)}</h2>
              {tx(trasversale.text, lang) && (
                <p className="mt-4 text-sm sm:text-base text-white/75 leading-relaxed max-w-3xl">{tx(trasversale.text, lang)}</p>
              )}
            </div>
          </Reveal>
        )}

        <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId="macroarea-cta-finale">
          <div className="bg-surface border border-royal/25 rounded-2xl p-8 lg:p-12">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice max-w-2xl">
              {tx(cta.heading, lang) || (lang === "it" ? "Inizia dall'analisi. Non da un'offerta." : "Start with analysis. Not an offer.")}
            </h2>
            {tx(cta.text, lang) && <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl">{tx(cta.text, lang)}</p>}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                data-testid="macroarea-cta"
                to="/#contatti"
                className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full bg-royal hover:bg-azure hover:text-[#0B1424] text-white transition-colors"
              >
                {tx(cta.primary, lang) || (lang === "it" ? "Parla con Ethic" : "Talk to Ethic")}
                <ArrowRight size={15} />
              </Link>
              {tx(cta.secondary, lang) && (
                <Link
                  data-testid="macroarea-cta-secondary"
                  to="/#contatti"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full border border-ice/15 text-ice hover:border-royal hover:text-royal transition-colors"
                >
                  {tx(cta.secondary, lang)}
                </Link>
              )}
            </div>
          </div>
        </Reveal>

        <div className="border-t border-ice/8 pt-10 pb-6">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
            {lang === "it" ? "Altre soluzioni" : "Other solutions"}
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(content.macroaree || {})
              .filter(([k]) => k !== id)
              .map(([k, a]) => (
                <Link
                  key={k}
                  to={`/soluzioni/${k}`}
                  data-testid={`macroarea-nav-${k}`}
                  className="px-4 py-2 rounded-full text-sm border border-ice/10 text-muted-foreground hover:text-ice hover:border-royal/50 transition-colors"
                >
                  {tx(a.title, lang)}
                </Link>
              ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-ice/5">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <p className="font-mono text-xs text-muted-foreground/60">
            © {new Date().getFullYear()} RR Solutions srl — ETHIC ENERGY · P.IVA 02582720997
          </p>
        </div>
      </footer>
    </div>
  );
}
